import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';

import { AppConfig } from '../../config/appConfig';
import { useListContext } from '../../contexts/listContext';

import { IMaterialPrice, IMaterialSummary } from '../../types/material';
import { IPinkSheetData, IShipmentExpense, IPinkSheetLoad, IPinkSheetCustomer, IPinkSheetVendor, IShipmentArap, IShipmentLight, IShipmentMaterial } from '../../types/shipment';

import { UtilityService } from '../../services/utilityService';

import { Decimal } from '../../decimal';
import { Icons } from '../icons';
import Json from '.././json';
import Spinner from '.././spinner';

import '../../styles/shipmentMaterialSummaryTable.scss';

export interface IMaterialSummaryProps {
	shipment: IShipmentLight;
	materials: IShipmentMaterial[];
	isLoadingMaterials: boolean;
	materialPrices: IMaterialPrice[];
	isLoadingMaterialPrices: boolean;
	pinkSheetData: IPinkSheetData;
	isLoadingPinkSheetData: boolean;
}

const ShipmentMaterialSummaryTable: React.FunctionComponent<IMaterialSummaryProps> = ({ shipment, materials, isLoadingMaterials, materialPrices, isLoadingMaterialPrices, pinkSheetData, isLoadingPinkSheetData }) => {
	const listContext = useListContext();
	const [shipmentExpenses, setShipmentExpenses] = useState<IShipmentExpense[]>([]);
	const [totalRoundedExpenseAmount, setTotalRoundedExpenseAmount] = useState(0);
	const [totalExpenseAmount, setTotalExpenseAmount] = useState(0);
	const [totalRoundedMaterialCostCredit, setTotalRoundedMaterialCostCredit] = useState(0);
	const [totalMaterialCostCredit, setTotalMaterialCostCredit] = useState(0);
	const [materialSummaryList, setMaterialSummaryList] = useState<IMaterialSummary[]>([]);
	const [totalMaterialWeightLbs, setTotalMaterialWeightLbs] = useState(0);
	const [totalTareWeightLbs, setTotalTareWeightLbs] = useState(0);
	const [totalQuantity, setTotalQuantity] = useState(0);

	const [vendor, setVendor] = useState<IPinkSheetVendor | undefined>();
	const [customer, setCustomer] = useState<IPinkSheetCustomer | undefined>();
	const [load, setLoad] = useState<IPinkSheetLoad | undefined>();
	const [showSummary, setShowSummary] = useState(false);

	// Formatters for numbers with max allowed and rounded (for money) of precision, rounding half away from zero
	const precFull = AppConfig.precisionDecimalsMaximum;
	const precRounded = AppConfig.precisionDecimalsRounded;
	const localeFull = new Intl.NumberFormat('en-US', { maximumFractionDigits: precFull, roundingMode: 'halfExpand' });
	const localeRounded = new Intl.NumberFormat('en-US', { maximumFractionDigits: precRounded, roundingMode: 'halfExpand' });

	const ref = useRef(null);

	useEffect(() => {
		if (!shipment || !pinkSheetData) return;
		const expenses: IShipmentExpense[] = [];
		let _totalRoundedExpenseAmount = 0;
		let _totalExpenseAmount = 0;

		pinkSheetData.shipment.expenses.map((item) => {
			let e: IShipmentExpense = {
				amount: item.isCost ? item.amount * -1 : item.amount,
				id: item.id,
				shipmentId: item.shipmentId,
				typeId: item.typeId,
				typeName: item.typeName,
				notes: item.notes,
				isCost: item.isCost
			}

			expenses.push(e);
			if (item.isCost) {
				_totalExpenseAmount -= item.amount;
				_totalRoundedExpenseAmount -= parseFloat(new Decimal(item?.amount ?? 0).toFixed(precRounded));
			}
			else {
				_totalExpenseAmount += item.amount;
				_totalRoundedExpenseAmount += parseFloat(new Decimal(item?.amount ?? 0).toFixed(precRounded));
			}
		})
		setShipmentExpenses(expenses.sort((a, b) => UtilityService.compareStrings(a.typeName, b.typeName)));
		setTotalExpenseAmount(_totalExpenseAmount);
		setTotalRoundedExpenseAmount(_totalRoundedExpenseAmount);
	}, [shipment, pinkSheetData])

	useEffect(() => {
		if (!pinkSheetData) return;
		setLoad(pinkSheetData.load);
		setVendor(pinkSheetData.vendor);
		setCustomer(pinkSheetData.customer);

	}, [pinkSheetData])

	useEffect(() => {
		let summaryList: IMaterialSummary[] = [];
		let totalRoundedCredit: number = 0;
		let totalCredit: number = 0;
		let totalMaterialWeight: number = 0;
		let totalTareWeight: number = 0;
		let totalQuantity: number = 0;
		if (!materials) return;

		materials.map((listItem) => {
			const vendorUomConversion = convertToPounds(1, listItem.vendorUOMAbbreviation) ?? 1; // This is kind of gross but cheap
			const priceByContainer = listItem.vendorMaterialPricePriceByContainer ? listContext.containers.find(c => c.id == listItem.vendorMaterialPriceContainerId) : undefined;
			const itemNetMaterialWeight = listItem.vendorAmount;
			const itemNetMaterialWeightInPounds = convertToPounds(itemNetMaterialWeight, listItem.vendorUOMAbbreviation);
			const itemContainerWeightInPounds = listItem.containerWeight * (listItem.quantity ?? 1);

			const results = summaryList.filter(
				(summaryItem) =>
					listItem.vendorMaterialId == summaryItem.materialId &&
					listItem.vendorMaterialPriceProfile == summaryItem.materialProfile &&
					listItem.plantUOMAbbreviation == summaryItem.uomAbbreviation);
			if (results.length == 0) {
				summaryList.push({
					"materialCategory": listItem.vendorMaterialPriceCategory,
					"quantity": listItem.quantity ?? 1,
					"materialName": listItem.vendorMaterialName,
					"materialId": listItem.vendorMaterialId,
					"materialProfile": listItem.vendorMaterialPriceProfile,
					"materialWeightOriginal": itemNetMaterialWeight,
					"materialWeightLbs": itemNetMaterialWeightInPounds,
					"materialRate": listItem.vendorMaterialPricePriceByContainer ? listItem.vendorMaterialPriceContainerPrice : listItem.vendorCreditPerUnit,
					"materialRateUnit": listItem.vendorMaterialPricePriceByContainer ? "container" : listItem.vendorCreditUnitAbbreviation ?? "Unknown",
					"materialRateUnitContainerName": listItem.vendorMaterialPricePriceByContainer ? priceByContainer?.name ?? `Unknown container ID ${listItem.vendorMaterialPriceContainerId?.toString()}` : undefined,
					"tareWeightLbs": listItem.vendorMaterialPriceIsGrossWeightMaterial ? 0 : itemContainerWeightInPounds,
					'isGrossWeightMaterial': listItem.vendorMaterialPriceIsGrossWeightMaterial,
					'isPriceByContainer': listItem.vendorMaterialPricePriceByContainer ?? false,
					'isTotalOverridden': false,
					"uomAbbreviation": listItem.vendorUOMAbbreviation,
					"uomConversion": vendorUomConversion,
					"uomName": listItem.vendorUOMName,
					"creditTotal": listItem.vendorCreditTotal,
					"materials": [listItem],
					"areMaterialsExpanded": false
				})
			}
			else {
				results[0].quantity += listItem.quantity ?? 1;
				results[0].materialWeightOriginal += itemNetMaterialWeight;
				results[0].materialWeightLbs += itemNetMaterialWeightInPounds;
				results[0].tareWeightLbs += listItem.vendorMaterialPriceIsGrossWeightMaterial ? 0 : itemContainerWeightInPounds;
				results[0].creditTotal += listItem?.vendorCreditTotal;
				results[0].materials?.push(listItem);
			}
		});

		summaryList.map((item) => {
			item.isTotalOverridden = item.materials?.some(subitem => subitem.vendorCreditTotalOverridden ?? false);
			totalRoundedCredit += parseFloat(new Decimal(item?.creditTotal ?? 0).toFixed(precRounded));
			totalCredit += item?.creditTotal;
			totalMaterialWeight += convertToPounds(item?.materialWeightOriginal, item?.uomAbbreviation);;
			totalTareWeight += item?.tareWeightLbs;
			totalQuantity += item?.quantity;
		})

		setTotalRoundedMaterialCostCredit(totalRoundedCredit);
		setTotalMaterialCostCredit(totalCredit);
		setTotalMaterialWeightLbs(totalMaterialWeight);
		setTotalTareWeightLbs(totalTareWeight);
		setTotalQuantity(totalQuantity);
		setMaterialSummaryList(summaryList.sort((a, b) => UtilityService.compareTwoFields(a, b, 'materialName', 'materialProfile', 'string', 'number')));
	}, [materials])

	const convertToPounds = (amount: number, uom: string) => {
		switch (uom) {
			case "units": {
				return 0;
			}
			case "transportation": {
				return 0;
			}
			case "lbs": {
				return amount;
			}
			case "gal": {
				return amount * 7.46;
			}
			case "tn": {
				return amount * 2000;
			}
			case "lt": {
				return amount * 2240;
			}
			case "mt": {
				return amount * 2204.62;
			}
			case "kg": {
				return amount * 2.20462;
			}
			default: {
				return amount;
			}
		}
	}

	useEffect(() => {
		let show = true;
		if (!shipment || isLoadingMaterials || isLoadingPinkSheetData) {
			show = false;
		}

		setShowSummary(show);
	}, [shipment, isLoadingMaterials, isLoadingPinkSheetData])

	return (
		<>
			<Spinner dependency={!showSummary} text={<>Loading Shipment Material Summary...</>}>
				<div className="hwi-shipment-material-summary-table">
					{/* <div className="print-button">
						<Button onClick={handlePrintClick}>Print Shipment Material Summary</Button>
					</div> */}
					<div className="wrapper" ref={ref}>
						<section className="content">
							<div className="title">
								<p>Shipment Material Summary for {shipment?.hwiNumber}</p>
								<div className="legend">
									<div className="sample-row">
										<div className="sample pink-sheet"></div>
										<span>Displayed on the Pink Sheet</span>
									</div>
									{materialSummaryList?.some(item => item.isTotalOverridden) &&
										<div className="sample-row">
											<div className="sample overridden"></div>
											<span>Overridden total</span>
										</div>
									}
								</div>
							</div>
							<div className="materials">
								<div className="row group">
									<div className="col blank-start"></div>
									<div className="col weight">Material Weight</div>
									<div className="col credit">Cost / Credit</div>
								</div>
								<div className="row header">
									<div className="col qty">QTY</div>
									<div className="col material-col">Material Description</div>
									<div className="col weight text-right">Original</div>
									<div className="col weight text-right">As Lbs</div>
									<div className="col tare">Tare</div>
									<div className="col total">Total</div>
									<div className="col rate-col text-right">Rate</div>
									<div className="col per-col">UOM</div>
									<div className="col credit">Full</div>
									<div className="col credit">Rounded</div>
									<div className="col diff">Diff</div>
								</div>
								{materialSummaryList.map((item, index) => (
									<React.Fragment key={'m' + index}>
										<div className="row item">
											<div 
												className={'col qty' + (item?.materials?.length > 1 ? ' expandable' : '')}
												onMouseDown={(e) => { if (e.button === 0 && item?.materials?.length > 1) { item.areMaterialsExpanded = !item.areMaterialsExpanded; setMaterialSummaryList([...materialSummaryList]) } }}
											>
												<div className="qty-container">
													<div className="expander-control">
														{item?.materials?.length > 1 &&
															(item?.areMaterialsExpanded ? (
																<div className="icon">
																	<Icons.Up height="14px" fill="#000000"></Icons.Up>
																</div>
															) : (
																<div className="icon">
																	<Icons.Down height="14px" fill="#000000"></Icons.Down>
																</div>
															))
														}
													</div>
													<div className="amount">{item?.quantity}</div>
												</div>
											</div>
											<div className="col material-col">{item?.materialName}</div>
											<div className={`col weight text-right${(item?.uomConversion ?? 1) == 1 ? ' inactive' : ''}`}>{localeFull.format((new Decimal(item?.materialWeightOriginal ?? 0)).toNumber())} {item?.uomAbbreviation}</div>
											<div className="col weight text-right">
												<Tooltip
													title={`1 ${item?.uomAbbreviation} = ${(item?.uomConversion ?? 1)} lbs`}
													placement='right'
													disableHoverListener={item?.uomAbbreviation == "lbs"}
													arrow
												>
													<span>{localeRounded.format(item?.materialWeightLbs ?? 0)} lbs</span>
												</Tooltip>
											</div>
											<div className="col tare">{item?.isGrossWeightMaterial ? "*" : `${localeRounded.format(item?.tareWeightLbs ?? 0)} lbs`}</div>
											<div className="col total">{localeRounded.format(((item?.materialWeightLbs ?? 0) + (item?.isGrossWeightMaterial ? 0 : (item?.tareWeightLbs ?? 0))))} lbs</div>
											<div className="col rate-col text-right">{item?.materialRate}</div>
											<div className="col per-col">
												<Tooltip
													title={item?.materialRateUnitContainerName ?? "Unknown Container"}
													placement='right'
													disableHoverListener={!item?.isPriceByContainer}
													arrow
												>
													<span>{item?.materialRateUnit}</span>
												</Tooltip>
											</div>
											<div className={`col credit${item?.isTotalOverridden ? " overridden" : ""}`}>{new Decimal(item?.creditTotal ?? 0).toDecimalPlaces(precFull).toString()}</div>
											<div className="col credit-rounded">{new Decimal(item?.creditTotal ?? 0).toFixed(precRounded)}</div>
											<div className={`col diff${!(new Decimal(item?.creditTotal ?? 0).toDecimalPlaces(precFull).sub(new Decimal(item?.creditTotal ?? 0).toDecimalPlaces(precRounded)).eq(0)) ? " nonzero-diff" : " inactive"}`}>
												{(new Decimal(item?.creditTotal ?? 0).sub(new Decimal(item?.creditTotal ?? 0).toDecimalPlaces(precRounded))).toDecimalPlaces(precFull).toString()}
											</div>
										</div>
										{item?.areMaterialsExpanded && item?.materials?.length > 0 &&
											item.materials.map((subitem, subindex) => (
												<div className={`row subitem${subindex == 0 ? " first" : ""}`} key={'m' + index + 's' + subindex}>
													<div className="col qty"></div>
													<div className="col material-col"></div>
													<div className={`col weight text-right${(item?.uomConversion ?? 1) == 1 ? ' inactive' : ''}`}>{localeFull.format((new Decimal(subitem?.vendorAmount ?? 0)).toNumber())} {item?.uomAbbreviation}</div>
													<div className="col weight text-right">{localeRounded.format(convertToPounds(subitem?.vendorAmount ?? 0, subitem?.vendorUOMAbbreviation))} lbs</div>
													<div className="col tare">{item?.isGrossWeightMaterial ? "*" : `${localeRounded.format(subitem?.containerWeight ?? 0)} lbs`}</div>
													<div className="col total">{localeRounded.format((convertToPounds(subitem?.vendorAmount ?? 0, subitem?.vendorUOMAbbreviation) + (item?.isGrossWeightMaterial ? 0 : (subitem?.containerWeight ?? 0))))} lbs</div>
													<div className="col rate-col text-right">{item?.materialRate}</div>
													<div className="col per-col">{UtilityService.singularize(item?.materialRateUnit)}</div>
													<div className={`col credit${subitem?.vendorCreditTotalOverridden ? " overridden" : ""}`}>{new Decimal(subitem?.vendorCreditTotal ?? 0).toDecimalPlaces(precFull).toString()}</div>
													<div className="col credit-rounded">{new Decimal(subitem?.vendorCreditTotal ?? 0).toFixed(precRounded)}</div>
													<div className={`col diff${!(new Decimal(subitem?.vendorCreditTotal ?? 0).toDecimalPlaces(precFull).sub(new Decimal(subitem?.vendorCreditTotal ?? 0).toDecimalPlaces(precRounded)).eq(0)) ? " nonzero-diff" : " inactive"}`}>
														{(new Decimal(subitem?.vendorCreditTotal ?? 0).sub(new Decimal(subitem?.vendorCreditTotal ?? 0).toDecimalPlaces(precRounded))).toDecimalPlaces(precFull).toString()}
													</div>
												</div>
											))}
									</React.Fragment>
								))}
								{shipmentExpenses.map((item, index) => (
									<div className="row item" key={'e' + index}>
										<div className="col qty"></div>
										<div className="col material-col">{item?.typeName}</div>
										<div className="col weight text-right inactive">-</div>
										<div className="col weight text-right inactive">-</div>
										<div className="col tare inactive">-</div>
										<div className="col total inactive">-</div>
										<div className="col rate-col text-right"></div>
										<div className="col per-col"></div>
										<div className="col credit">{new Decimal(item?.amount ?? 0).toDecimalPlaces(precFull).toString()}</div>
										<div className="col credit-rounded">{new Decimal(item?.amount ?? 0).toFixed(precRounded)}</div>
										<div className={`col diff${!(new Decimal(item?.amount ?? 0).toDecimalPlaces(precFull).sub(new Decimal(item?.amount ?? 0).toDecimalPlaces(precRounded)).eq(0)) ? "" : " inactive"}`}>
											{(new Decimal(item?.amount ?? 0).sub(new Decimal(item?.amount ?? 0).toDecimalPlaces(precRounded))).toDecimalPlaces(precFull).toString()}
										</div>
									</div>
								))}
								<div className="row totals">
									<div className="col qty">
										<div className="qty-container">
											<div></div>
											<div className="amount">{totalQuantity}</div>											
										</div>
									</div>
									<div className="col material-col">Full Totals</div>
									<div className="col weight text-right"></div>
									<div className="col weight text-right">{localeRounded.format(totalMaterialWeightLbs ?? 0)} lbs</div>
									<div className="col tare">{localeRounded.format(totalTareWeightLbs ?? 0)} lbs</div>
									<div className="col total">{localeRounded.format((totalMaterialWeightLbs ?? 0) + (totalTareWeightLbs ?? 0))} lbs</div>
									<div className="col rate-col text-right"></div>
									<div className="col per-col"></div>
									<div className="col credit">{new Decimal(totalMaterialCostCredit + totalExpenseAmount).toDecimalPlaces(precFull).toString()}</div>
									<div className="col credit-rounded">{new Decimal(totalRoundedMaterialCostCredit + totalRoundedExpenseAmount).toDecimalPlaces(precFull).toString()}</div>
									<div className={`col diff${!(new Decimal(totalMaterialCostCredit + totalExpenseAmount).sub(new Decimal(totalRoundedMaterialCostCredit + totalRoundedExpenseAmount).toDecimalPlaces(precRounded)).eq(0)) ? "" : " inactive"}`}>
										{(new Decimal(totalMaterialCostCredit + totalExpenseAmount).sub(new Decimal(totalRoundedMaterialCostCredit + totalRoundedExpenseAmount).toDecimalPlaces(precRounded))).toDecimalPlaces(precFull).toString()}
									</div>
								</div>
								<div className="row rounded-totals">
									<div className="col qty">
										<div className="qty-container">
											<div></div>
											<div className="amount">{totalQuantity}</div>
										</div>
									</div>
									<div className="col material-col">Rounded Totals</div>
									<div className="col weight text-right"></div>
									<div className="col weight text-right">{localeFull.format(totalMaterialWeightLbs ?? 0)} lbs</div>
									<div className="col tare">{localeFull.format(totalTareWeightLbs ?? 0)} lbs</div>
									<div className="col total">{localeFull.format((totalMaterialWeightLbs ?? 0) + (totalTareWeightLbs ?? 0))} lbs</div>
									<div className="col rate-col text-right"></div>
									<div className="col per-col"></div>
									<div className="col credit">{new Decimal(totalMaterialCostCredit + totalExpenseAmount).toFixed(precRounded)}</div>
									<div className="col credit-rounded">{new Decimal(totalRoundedMaterialCostCredit + totalRoundedExpenseAmount).toFixed(precRounded)}</div>
									<div className={`col diff${!(new Decimal(totalMaterialCostCredit + totalExpenseAmount).toDecimalPlaces(precRounded).sub(new Decimal(totalRoundedMaterialCostCredit + totalRoundedExpenseAmount).toDecimalPlaces(precRounded)).eq(0)) ? " nonzero-diff" : " inactive"}`}>
										{(new Decimal(totalMaterialCostCredit + totalExpenseAmount).toDecimalPlaces(precRounded).sub(new Decimal(totalRoundedMaterialCostCredit + totalRoundedExpenseAmount).toDecimalPlaces(precRounded))).toString()}
									</div>
								</div>
								{materialSummaryList?.some(m => m.isGrossWeightMaterial) &&
									<div className="gross-weight-materials-warning">
										<p>{AppConfig.grossWeightMaterialMessage}</p>
									</div>
								}
							</div>
						</section>
					</div>
					{/* <Json data={materialSummaryList}></Json> */}
					{/* <Json data={load} title="Load:"></Json>  */}
				</div >
			</Spinner >
		</>
	)
}

export default ShipmentMaterialSummaryTable;